import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth, hasPagePermission, isPageDenied } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import api from './services/api';

// Pages
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { HomePage } from './pages/HomePage';
import { ReportsListPage } from './pages/ReportsListPage';
import { ReportDetailPage } from './pages/ReportDetailPage';
import { NewReportPage } from './pages/NewReportPage';
import { EditReportPage } from './pages/EditReportPage';
import { AdminPage } from './pages/AdminPage';
import { CategoriesAdminPage } from './pages/CategoriesAdminPage';
import { StatusesAdminPage } from './pages/StatusesAdminPage';
import { FormBuilderPage } from './pages/FormBuilderPage';
import { UsersAdminPage } from './pages/UsersAdminPage';
import { RolesAdminPage } from './pages/RolesAdminPage';
import SystemLogPage from './pages/SystemLogPage';
import { SensorikaPage } from './pages/SensorikaPage';
import { NewSensorikaPage } from './pages/NewSensorikaPage';
import { SensorikaDetailPage } from './pages/SensorikaDetailPage';
import { SensoryAdminPage } from './pages/SensoryAdminPage';
import { SensoryTeamPage } from './pages/SensoryTeamPage';
import { SensoryTeamSchedulePage } from './pages/SensoryTeamSchedulePage';
import { SensoryDailyReportAdminPage } from './pages/SensoryDailyReportAdminPage';
import SensoryAnalyticsPage from './pages/SensoryAnalyticsPage';
import { NoPermissionPage } from './pages/NoPermissionPage';
// Autoclave pages
import AutoclavePage from './pages/AutoclavePage';
import AutoclaveDetailPage from './pages/AutoclaveDetailPage';
import AutoclaveDevicesPage from './pages/AutoclaveDevicesPage';
import AutoclaveProgramsPage from './pages/AutoclaveProgramsPage';
import AutoclaveProcessFieldsPage from './pages/AutoclaveProcessFieldsPage';
// Batch search
import BatchSearchPage from './pages/BatchSearchPage';
// Tasks
import TasksPage from './pages/TasksPage';
import TaskEventDetailPage from './pages/TaskEventDetailPage';
import TaskNotificationAdminPage from './pages/TaskNotificationAdminPage';
// Audits
import AuditsPage from './pages/AuditsPage';
import NewAuditPage from './pages/NewAuditPage';
import AuditDetailPage from './pages/AuditDetailPage';
import AuditHeaderAdminPage from './pages/AuditHeaderAdminPage';
import MonitoringAdminPage from './pages/MonitoringAdminPage';
// Production
import ProductionPage from './pages/ProductionPage';
// Balírna
import BalirnaPage from './pages/BalirnaPage';
import BalirnaDetailPage from './pages/BalirnaDetailPage';
import BalirnaVerifyPage from './pages/BalirnaVerifyPage';
import BalirnaAdminPage from './pages/BalirnaAdminPage';
// Kuchyně
import KitchenPage from './pages/KitchenPage';
import KitchenDetailPage from './pages/KitchenDetailPage';
import KitchenSettingsPage from './pages/KitchenSettingsPage';
import HandoverRecordsPage from './pages/HandoverRecordsPage';

// Redirect component for old sensory URLs (/senzorika/:id -> /senzorika/{type}/:id)
const SensoryRedirect = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAndRedirect = async () => {
      try {
        const response = await api.get(`/sensory/tests/${id}`);
        const sensoryType = response.data.sensory_type || 'hotovky';
        navigate(`/senzorika/${sensoryType}/${id}`, { replace: true });
      } catch (error) {
        // If test not found, redirect to hotovky list
        navigate('/senzorika/hotovky', { replace: true });
      }
    };
    fetchAndRedirect();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  return null;
};

const ProtectedRoute = ({ children, pageKey, pageName, requireEdit = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="text-slate-500">Načítání...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Úkoly a Inspekce jsou přístupné všem přihlášeným uživatelům
  if (pageKey === 'tasks' || pageKey === 'audits') {
    return children;
  }

  // Check page-level permissions if pageKey is provided
  if (pageKey) {
    // Check if explicitly denied
    if (isPageDenied(user, pageKey)) {
      return <NoPermissionPage pageName={pageName} />;
    }
    
    // Check if user has required permission level
    const requiredLevel = requireEdit ? 'edit' : 'view';
    if (!hasPagePermission(user, pageKey, requiredLevel)) {
      return <NoPermissionPage pageName={pageName} />;
    }
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute pageKey="reports" pageName="Hlášení">
            <ReportsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/new"
        element={
          <ProtectedRoute pageKey="reports" pageName="Nové hlášení" requireEdit>
            <NewReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/:id/edit"
        element={
          <ProtectedRoute pageKey="reports" pageName="Úprava hlášení" requireEdit>
            <EditReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/:id"
        element={
          <ProtectedRoute pageKey="reports" pageName="Detail hlášení">
            <ReportDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute pageKey="admin" pageName="Administrace">
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute pageKey="admin" pageName="Správa uživatelů" requireEdit>
            <UsersAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/roles"
        element={
          <ProtectedRoute pageKey="admin" pageName="Správa rolí" requireEdit>
            <RolesAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/system-log"
        element={
          <ProtectedRoute pageKey="admin" pageName="Systémové logy">
            <SystemLogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/monitoring"
        element={
          <ProtectedRoute pageKey="admin" pageName="Server Monitoring">
            <MonitoringAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/categories"
        element={
          <ProtectedRoute pageKey="admin" pageName="Kategorie" requireEdit>
            <CategoriesAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/statuses"
        element={
          <ProtectedRoute pageKey="admin" pageName="Stavy" requireEdit>
            <StatusesAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/form-builder"
        element={
          <ProtectedRoute pageKey="admin" pageName="Editor formuláře" requireEdit>
            <FormBuilderPage />
          </ProtectedRoute>
        }
      />
      {/* Redirect /senzorika to /senzorika/hotovky */}
      <Route
        path="/senzorika"
        element={<Navigate to="/senzorika/hotovky" replace />}
      />
      {/* Legacy route - redirect old /senzorika/:id to correct path */}
      <Route
        path="/senzorika/:id"
        element={
          <ProtectedRoute>
            <SensoryRedirect />
          </ProtectedRoute>
        }
      />
      {/* Hotovky routes */}
      <Route
        path="/senzorika/hotovky"
        element={
          <ProtectedRoute pageKey="sensory_hotovky" pageName="Senzorika Hotovky">
            <SensorikaPage sensoryType="hotovky" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/senzorika/hotovky/new"
        element={
          <ProtectedRoute pageKey="sensory_hotovky" pageName="Nová zkouška Hotovky" requireEdit>
            <NewSensorikaPage sensoryType="hotovky" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/senzorika/hotovky/tym"
        element={
          <ProtectedRoute pageKey="sensory_hotovky" pageName="Rozpis týmu Hotovky">
            <SensoryTeamSchedulePage sensoryType="hotovky" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/senzorika/hotovky/:id"
        element={
          <ProtectedRoute pageKey="sensory_hotovky" pageName="Detail zkoušky Hotovky">
            <SensorikaDetailPage sensoryType="hotovky" />
          </ProtectedRoute>
        }
      />
      {/* Krabicky routes */}
      <Route
        path="/senzorika/krabicky"
        element={
          <ProtectedRoute pageKey="sensory_krabicky" pageName="Senzorika Krabičky">
            <SensorikaPage sensoryType="krabicky" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/senzorika/krabicky/new"
        element={
          <ProtectedRoute pageKey="sensory_krabicky" pageName="Nová zkouška Krabičky" requireEdit>
            <NewSensorikaPage sensoryType="krabicky" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/senzorika/krabicky/tym"
        element={
          <ProtectedRoute pageKey="sensory_krabicky" pageName="Rozpis týmu Krabičky">
            <SensoryTeamSchedulePage sensoryType="krabicky" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/senzorika/krabicky/:id"
        element={
          <ProtectedRoute pageKey="sensory_krabicky" pageName="Detail zkoušky Krabičky">
            <SensorikaDetailPage sensoryType="krabicky" />
          </ProtectedRoute>
        }
      />
      {/* Sensory Analytics */}
      <Route
        path="/senzorika/analytika"
        element={
          <ProtectedRoute pageKey="sensory_hotovky" pageName="Hodnocení senzorik">
            <SensoryAnalyticsPage />
          </ProtectedRoute>
        }
      />
      {/* Admin Routes - Sensory split by type */}
      <Route
        path="/admin/sensory"
        element={<Navigate to="/admin/sensory/hotovky" replace />}
      />
      <Route
        path="/admin/sensory/hotovky"
        element={
          <ProtectedRoute pageKey="admin" pageName="Nastavení Senzorik Hotovky" requireEdit>
            <SensoryAdminPage sensoryType="hotovky" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sensory/krabicky"
        element={
          <ProtectedRoute pageKey="admin" pageName="Nastavení Senzorik Krabičky" requireEdit>
            <SensoryAdminPage sensoryType="krabicky" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sensory-team"
        element={<Navigate to="/admin/sensory-team/hotovky" replace />}
      />
      <Route
        path="/admin/sensory-team/hotovky"
        element={
          <ProtectedRoute pageKey="admin" pageName="Senzorický tým Hotovky" requireEdit>
            <SensoryTeamPage sensoryType="hotovky" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sensory-team/krabicky"
        element={
          <ProtectedRoute pageKey="admin" pageName="Senzorický tým Krabičky" requireEdit>
            <SensoryTeamPage sensoryType="krabicky" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sensory-daily-report"
        element={<Navigate to="/admin/sensory-daily-report/hotovky" replace />}
      />
      <Route
        path="/admin/sensory-daily-report/hotovky"
        element={
          <ProtectedRoute pageKey="admin" pageName="Denní report Hotovky" requireEdit>
            <SensoryDailyReportAdminPage sensoryType="hotovky" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sensory-daily-report/krabicky"
        element={
          <ProtectedRoute pageKey="admin" pageName="Denní report Krabičky" requireEdit>
            <SensoryDailyReportAdminPage sensoryType="krabicky" />
          </ProtectedRoute>
        }
      />
      {/* Autoclave Routes */}
      <Route
        path="/autoklav"
        element={
          <ProtectedRoute pageKey="autoclave" pageName="Autokláv">
            <AutoclavePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/autoklav/:id"
        element={
          <ProtectedRoute pageKey="autoclave" pageName="Detail autoklávu">
            <AutoclaveDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/autoclave-devices"
        element={
          <ProtectedRoute pageKey="admin" pageName="Autoklávy - číselník" requireEdit>
            <AutoclaveDevicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/autoclave-programs"
        element={
          <ProtectedRoute pageKey="admin" pageName="Programy autoklávu" requireEdit>
            <AutoclaveProgramsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/autoclave-process-fields"
        element={
          <ProtectedRoute pageKey="admin" pageName="Záznamy procesu autoklávu" requireEdit>
            <AutoclaveProcessFieldsPage />
          </ProtectedRoute>
        }
      />
      {/* Batch Search */}
      <Route
        path="/sarze"
        element={
          <ProtectedRoute pageKey="batch_search" pageName="Detail šarže">
            <BatchSearchPage />
          </ProtectedRoute>
        }
      />
      {/* Tasks */}
      <Route
        path="/ukoly"
        element={
          <ProtectedRoute pageKey="tasks" pageName="Úkoly">
            <TasksPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ukoly/:eventId"
        element={
          <ProtectedRoute pageKey="tasks" pageName="Detail události">
            <TaskEventDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/task-notifications"
        element={
          <ProtectedRoute pageKey="admin" pageName="Nastavení notifikací úkolů" requireEdit>
            <TaskNotificationAdminPage />
          </ProtectedRoute>
        }
      />
      {/* Audits */}
      <Route
        path="/audity"
        element={
          <ProtectedRoute pageKey="audits" pageName="Interní audity">
            <AuditsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audity/novy"
        element={
          <ProtectedRoute pageKey="audits" pageName="Nový audit" requireEdit>
            <NewAuditPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audity/:auditId"
        element={
          <ProtectedRoute pageKey="audits" pageName="Detail auditu">
            <AuditDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/audit-header"
        element={
          <ProtectedRoute pageKey="admin" pageName="Hlavička auditu" requireEdit>
            <AuditHeaderAdminPage />
          </ProtectedRoute>
        }
      />
      {/* Production */}
      <Route
        path="/vyroba"
        element={
          <ProtectedRoute pageKey="production" pageName="Výroba">
            <ProductionPage />
          </ProtectedRoute>
        }
      />
      {/* Balírna */}
      <Route
        path="/balirna"
        element={
          <ProtectedRoute pageKey="balirna" pageName="Balírna">
            <BalirnaPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/balirna/new/:productionId"
        element={
          <ProtectedRoute pageKey="balirna" pageName="Nový kontrolní list" requireEdit>
            <BalirnaDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/balirna/overit/:productionId"
        element={
          <ProtectedRoute pageKey="balirna" pageName="Převzetí z kuchyně" requireEdit>
            <BalirnaVerifyPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/balirna/:id"
        element={
          <ProtectedRoute pageKey="balirna" pageName="Kontrolní list">
            <BalirnaDetailPage />
          </ProtectedRoute>
        }
      />
      {/* Kuchyně */}
      <Route
        path="/kuchyne"
        element={
          <ProtectedRoute pageKey="kitchen" pageName="Kuchyně">
            <KitchenPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/kuchyne/:recordId"
        element={
          <ProtectedRoute pageKey="kitchen" pageName="Předávka">
            <KitchenDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/kuchyne"
        element={
          <ProtectedRoute pageKey="admin" pageName="Nastavení Kuchyně" requireEdit>
            <KitchenSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/zaznamy-predavky"
        element={
          <ProtectedRoute pageKey="handover_records" pageName="Záznamy předávky">
            <HandoverRecordsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/balirna"
        element={
          <ProtectedRoute pageKey="admin" pageName="Nastavení balírny" requireEdit>
            <BalirnaAdminPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;